import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useEmailValue, useItemValue, useLoginIdValue, useOrderValue, usePhoneNumberValue, useTab } from '@/hooks/tab';
import { Tab } from '@/types/hashes';
import { useItem } from '@/hooks/item';
import { useOrder } from '@/hooks/order';
import { useClientId, useClientEmail, useClientPhoneNumber } from '@/hooks/client';
import Logo from '@/components/molecules/header/Logo';
import Tabs from '@/components/molecules/header/Tabs';
import Search from '@/components/molecules/header/Search';
import styles from './Header.module.scss';
import {
  useCurrentEmailItem,
  useCurrentLoginIdItem,
  useCurrentOrderItem,
  useCurrentPhoneNumberItem,
} from '@/hooks/reference';
import { isPositiveNumber, isEmail, isValidPhoneNumber } from '@/utilities/validation';
import { formatPhoneNumber } from '@/utilities/client';
import DocumentView from '@/components/molecules/header/DocumentView';

const View: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useTab();

  const tabs = [
    { label: t('header.tabs.item'), active: tab === Tab.ITEM, onClick: () => setTab(Tab.ITEM), key: Tab.ITEM },
    { label: t('header.tabs.order'), active: tab === Tab.ORDER, onClick: () => setTab(Tab.ORDER), key: Tab.ORDER },
    { label: t('header.tabs.email'), active: tab === Tab.EMAIL, onClick: () => setTab(Tab.EMAIL), key: Tab.EMAIL },
    {
      label: t('header.tabs.login_id'),
      active: tab === Tab.LOGIN_ID,
      onClick: () => setTab(Tab.LOGIN_ID),
      key: Tab.LOGIN_ID,
    },
    {
      label: t('header.tabs.phone_number'),
      active: tab === Tab.PHONE_NUMBER,
      onClick: () => setTab(Tab.PHONE_NUMBER),
      key: Tab.PHONE_NUMBER,
    },
  ];

  const getTabContentComp = () => {
    switch (tab) {
      // case Tab.ITEM:
      //   return <ItemSearch />;
      case Tab.ORDER:
        return <OrderSearch />;
      case Tab.EMAIL:
        return <EmailSearch />;
      case Tab.LOGIN_ID:
        return <LoginIdSearch />;
      case Tab.PHONE_NUMBER:
        return <PhoneNumberSearch />;
      default:
        return <ItemSearch />;
    }
  };

  return (
    <div
      className={styles.container}
      data-cy="Page-header"
    >
      <div className={styles.wrapper}>
        <Logo data-cy="Page-header__logo" />
        <div
          className={styles.searchContainer}
          data-cy="Page-header__search"
        >
          <div
            className={styles.tabs}
            data-cy="Page-header__tabs"
          >
            <Tabs tabs={tabs} />
          </div>
          <div
            className={styles.search}
            data-cy="Page-header__searchBar"
          >
            <div
              className={clsx(styles.searchBlock)}
              data-cy="Page-header__searchBar"
            >
              {getTabContentComp()}
            </div>
          </div>
        </div>
        <div className={styles.documentContainer}>
          <DocumentView />
        </div>
      </div>
    </div>
  );
};

const ItemSearch: React.FC = () => {
  const { t } = useTranslation();
  const placeholder = t('header.placeholder.item');
  const [inputValue, setInputValue] = useItemValue();
  const data = useItem(Number(inputValue));

  const refresh = () => {
    data.remove();
  };

  return (
    <ViewSearch
      placeholder={placeholder}
      inputValue={inputValue}
      setInputValue={setInputValue}
      refresh={refresh}
      isFetching={data.kind === 'Called' && data.fetching}
      validate={isPositiveNumber}
    />
  );
};

const OrderSearch: React.FC = () => {
  const { t } = useTranslation();
  const placeholder = t('header.placeholder.order');
  const [inputValue, setInputValue] = useOrderValue();
  const data = useOrder(Number(inputValue));
  const [, setCurrentItem] = useCurrentOrderItem();

  const refresh = () => {
    data.remove();
    setCurrentItem('');
  };

  return (
    <ViewSearch
      placeholder={placeholder}
      inputValue={inputValue}
      setInputValue={setInputValue}
      refresh={refresh}
      isFetching={data.kind === 'Called' && data.fetching}
      validate={isPositiveNumber}
    />
  );
};

const EmailSearch: React.FC = () => {
  const { t } = useTranslation();
  const placeholder = t('header.placeholder.email');
  const [inputValue, setInputValue] = useEmailValue();
  const data = useClientEmail(inputValue);
  const [, setCurrentItem] = useCurrentEmailItem();

  const refresh = () => {
    data.remove();
    setCurrentItem('');
  };

  return (
    <ViewSearch
      placeholder={placeholder}
      inputValue={inputValue}
      setInputValue={setInputValue}
      refresh={refresh}
      isFetching={data.kind === 'Called' && data.fetching}
      validate={isEmail}
    />
  );
};

const LoginIdSearch: React.FC = () => {
  const { t } = useTranslation();
  const placeholder = t('header.placeholder.login_id');
  const [inputValue, setInputValue] = useLoginIdValue();
  // It doesn't make sense to use useClientId here
  const data = useClientId(Number(inputValue));
  const [, setCurrentItem] = useCurrentLoginIdItem();

  const refresh = () => {
    data.remove();
    setCurrentItem('');
  };

  return (
    <ViewSearch
      placeholder={placeholder}
      inputValue={inputValue}
      setInputValue={setInputValue}
      refresh={refresh}
      isFetching={data.kind === 'Called' ? data.fetching : false}
      validate={isPositiveNumber}
      data-cy="searchBar-loginID"
    />
  );
};

const ViewSearch: React.FC<{
  placeholder: string;
  inputValue: string;
  setInputValue: (v: string) => void;
  refresh: () => void;
  isFetching: boolean;
  validate: (v: string) => boolean;
}> = ({ placeholder, inputValue, setInputValue, isFetching, validate }) => {
  const [value, setValue] = useState(inputValue);

  useEffect(() => {
    setValue(inputValue);
  }, [inputValue]);

  return (
    <Search
      placeholder={placeholder}
      value={value}
      setValue={setValue}
      loading={isFetching}
      validInput={validate(value)}
      search={() => {
        if (!isFetching) {
          // refresh();
          setInputValue(value);
        }
      }}
    />
  );
};

const PhoneNumberSearch: React.FC = () => {
  const { t } = useTranslation();
  const placeholder = t('header.placeholder.phone_number');
  const [inputValue, setInputValue] = usePhoneNumberValue();
  const data = useClientPhoneNumber(formatPhoneNumber(inputValue));
  const [, setCurrentItem] = useCurrentPhoneNumberItem();

  const refresh = () => {
    data.remove();
    setCurrentItem('');
  };

  return (
    <ViewSearch
      placeholder={placeholder}
      inputValue={inputValue}
      setInputValue={setInputValue}
      refresh={refresh}
      isFetching={data.kind === 'Called' && data.fetching}
      validate={isValidPhoneNumber}
    />
  );
};

export default View;
